import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useTimers } from "@src/appV2/lib/utils/useTimers";
import { useCallback } from "react";

import { type TimekeepingAction } from "../constants";

function buildTimerKey(action: TimekeepingAction, shiftId: string): string {
  return `timekeeping-action-${action}-${shiftId}`;
}

interface TrackTimekeepingActionCompletedProps {
  nonInstantPayReason?: string;
}

interface UseTimekeepingActionTimerResult {
  startTimekeepingActionTracking: () => void;
  completeTimekeepingActionTracking: (props?: TrackTimekeepingActionCompletedProps) => void;
}

interface UseTimekeepingActionTimerParams {
  action: TimekeepingAction;
  shiftId: string;
  workerId: string;
  workplaceId: string | undefined;
  timekeepingMethod: string;
}

export function useTimekeepingActionTimer(
  params: UseTimekeepingActionTimerParams
): UseTimekeepingActionTimerResult {
  const { action, shiftId, workerId, workplaceId, timekeepingMethod } = params;
  const { start, stop, isRunning } = useTimers();
  const timerKey = buildTimerKey(action, shiftId);

  const startTimekeepingActionTracking = useCallback(() => {
    if (!isRunning(timerKey)) {
      start(timerKey);
    }
  }, [isRunning, start, timerKey]);

  const completeTimekeepingActionTracking = useCallback(
    (props?: TrackTimekeepingActionCompletedProps) => {
      const duration = stop(timerKey);

      if (!isDefined(duration)) {
        return;
      }

      logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_ACTION_COMPLETED, {
        action,
        shiftId,
        workerId,
        workplaceId,
        timekeepingMethod,
        durationMs: duration.totalDurationMs,
        activeDurationMs: duration.activeDurationMs,
        ...props,
      });
    },
    [stop, timerKey, action, shiftId, workerId, workplaceId, timekeepingMethod]
  );

  return { startTimekeepingActionTracking, completeTimekeepingActionTracking };
}
